import { Icon } from '@iconify/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { DateRange, TransactionStatus, TransactionType, type Transaction } from '../../types/ApiTypes'
import type { FilterState } from '../../types/FilterStateInterface'
import type { TransactionData } from '../../types/TransactionDataType'
import FilterDropdown from '../components/FilterDropdown'
import Pagination from '../components/Pagination'
import SkeletonTable from '../components/SkeletonTable'
import TransactionDetails from '../components/TransactionDetails'
import TransactionList from '../components/TransactionList'
import { useApi } from '../hooks/useApi'
import Dashboard from '../layout/Dashboard'
import {
	Content,
	Heading,
} from '../styles/OverviewStyle'
import { formatDate } from '../utils/dateHelpers'
import { formatWithCommas } from '../utils/numberHelpers'

export default function Transactions(): React.JSX.Element {
	const [query, setQuery] = useState('')
	const [debouncedQuery, setDebouncedQuery] = useState('')
	const filterBtnRef = useRef<HTMLButtonElement | null>(null)
	const [filters, setFilters] = useState<FilterState>({ types: [], currencies: [], statuses: [], ranges: [] })
	const [open, setOpen] = useState(false)
	const [selected, setSelected] = useState<Transaction | null>(null)
	const [searchParams, setSearchParams] = useSearchParams()
	const { transaction: transactionService } = useApi()
	const [transactions, setTransactions] = useState<Transaction[]>([])
	const [loading, setLoading] = useState(true)
	const [currentPage, setCurrentPage] = useState(1)
	const [totalPages, setTotalPages] = useState(0)
	const pageSize = 20

	const formatTransactionType = (type: string): string => {
		return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
	}

	const mapTransactions = (txns: Transaction[]): TransactionData => {
		return txns.map(t => {
			const statusMap: Record<string, 'Completed' | 'Pending' | 'Failed'> = {
				completed: 'Completed',
				pending: 'Pending',
				failed: 'Failed'
			};

			let asset = 'N/A';
			let rate = 'N/A';

			if (t.giftcardPurchase) {
				asset = t.giftcardPurchase.brandName;
				rate = `₦${formatWithCommas(t.giftcardPurchase.ratePerDollar)}`;
			} else if (t.giftcardSale) {
				asset = t.giftcardSale.type;
				rate = `₦${formatWithCommas(t.giftcardSale.ratePerDollar)}`;
			} else if (t.cryptoPurchase) {
				asset = t.cryptoPurchase.currency.toUpperCase();
				rate = `₦${formatWithCommas(t.cryptoPurchase.ratePerDollar)}`;
			} else if (t.cryptoSale) {
				asset = t.cryptoSale.currency.toUpperCase();
				rate = `₦${formatWithCommas(t.cryptoSale.ratePerDollar)}`;
			} else if (t.cryptoDeposit) {
				asset = t.cryptoDeposit.currency.toUpperCase();
				rate = `₦${formatWithCommas(t.cryptoDeposit.ratePerDollar)}`;
			}

			return {
				ref: t.transactionId,
				user: `@${t.username}`,
				type: formatTransactionType(t.type),
				asset,
				amount: `₦${formatWithCommas(t.amount)}`,
				rate,
				status: statusMap[t.status] || 'Pending',
				date: formatDate(t.date)
			};
		});
	}

	const mapped = mapTransactions(transactions)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(query), 500)
		return () => clearTimeout(timer)
	}, [query])

	const buildFilters = useCallback(() => {
		const params: { pageNumber: number; pageSize: number; search?: string; type?: TransactionType; status?: TransactionStatus; dateRange?: DateRange } = { pageNumber: currentPage, pageSize }

		if (debouncedQuery) params.search = debouncedQuery

		if (filters.types.length > 0) {
			const typeMap: Record<string, TransactionType> = {
				'Crypto': TransactionType.CRYPTO_PURCHASE,
				'Giftcard': TransactionType.GIFTCARD_SALE,
				'Wallet Top-up': TransactionType.TOPUP,
				'Withdrawal': TransactionType.WITHDRAWAL
			}
			const firstType = filters.types[0]
			if (firstType && typeMap[firstType]) params.type = typeMap[firstType]
		}

		if (filters.statuses.length > 0) {
			const statusMap: Record<string, TransactionStatus> = {
				'Pending': TransactionStatus.PENDING,
				'Completed': TransactionStatus.COMPLETED,
				'Failed': TransactionStatus.FAILED
			}
			const firstStatus = filters.statuses[0]
			if (firstStatus && statusMap[firstStatus]) params.status = statusMap[firstStatus]
		}

		if (filters.ranges.length > 0) {
			const rangeMap: Record<string, DateRange> = {
				'Today': DateRange.TODAY,
				'This Week': DateRange.THIS_WEEK,
				'This Month': DateRange.THIS_MONTH
			}
			const firstRange = filters.ranges[0]
			if (firstRange && rangeMap[firstRange]) params.dateRange = rangeMap[firstRange]
		}

		return params
	}, [currentPage, debouncedQuery, filters.ranges, filters.statuses, filters.types])

	useEffect(() => {
		const loadTransactions = async () => {
			setLoading(true)
			const params = buildFilters()
			const res = await transactionService.getTransactions(params)
			if (res.status === 'success' && res.data) {
				setTransactions(res.data.transactions)
				setTotalPages(res.data.totalPages)
			}
			setLoading(false)
		}
		loadTransactions()
	}, [transactionService, currentPage, debouncedQuery, filters, buildFilters])

	useEffect(() => {
		const id = searchParams.get('txn')
		if (!id) {
			if (selected) setSelected(null)
			return
		}
		if (!selected || selected.transactionId !== id) {
			const txn = transactions.find(t => t.transactionId === id)
			if (txn) setSelected(txn)
		}
	}, [searchParams, selected, transactions])

	function handleSelect(t: TransactionData[number]) {
		setSearchParams(prev => {
			const p = new URLSearchParams(prev)
			p.set('txn', t.ref)
			return p
		})
	}

	function handleClose() {
		setSearchParams(prev => {
			if (!prev.get('txn')) return prev
			const p = new URLSearchParams(prev)
			p.delete('txn')
			return p
		})
	}

	return (
		<Dashboard title="Transaction / All" icon="akar-icons:reciept" active="transactions">
			<Content>
				<Heading>Transactions</Heading>

				<div className="flex items-center gap-4 mt-6 mb-4">
					<div className="flex items-center gap-2 bg-white border border-[#EDEDEE] rounded-md px-[9px] py-[6px] w-[229px] h-[28px]">
						<Icon icon='solar:magnifer-linear' width={14} className="text-[#B3B3B6] mb-0.5" />
						<input
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder="Search"
							className="flex-1 bg-transparent outline-none text-[12px] font-medium tracking-tight placeholder:text-[#B3B3B6]" />
					</div>

					<div className="relative">
						<button
							ref={filterBtnRef}
							onClick={() => setOpen(o => !o)}
							className="flex items-center gap-1.5 px-3 py-1.5 h-[28px] text-xs tracking-tight bg-white border border-[#E7E7E9] rounded-lg cursor-pointer hover:bg-[#F9F9FA] transition-colors"
						>
							<Icon icon='solar:sort-outline' width={16} />
							<span className='text-neutral-900 font-medium'>Filter</span>
						</button>

						<FilterDropdown
							open={open}
							onClose={() => setOpen(false)}
							value={filters}
							onChange={setFilters}
							anchorRef={filterBtnRef}
						/>
					</div>

					<button
						className="ml-auto flex items-center gap-1.5 px-3 py-1.5 h-[28px] text-xs tracking-tight bg-white border border-[#E7E7E9] rounded-lg cursor-pointer hover:bg-[#F9F9FA] transition-colors"
					>
						<span className='text-neutral-900 font-medium'>Export csv</span>
						<Icon icon='solar:upload-linear' width={14} />
					</button>
				</div>

				{loading ? <SkeletonTable /> : <TransactionList data={mapped} onSelect={handleSelect} />}

				<TransactionDetails open={!!selected} onClose={handleClose} txn={selected ?? undefined} />

				{!loading && totalPages > 0 && (
					<Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
				)}
			</Content>
		</Dashboard>
	)
}
